import { useState } from 'react';
import type { Customer } from '../services/customersApi';
import {
  useCustomerDiscounts,
  useCreateCustomerDiscount,
  useDeleteCustomerDiscount,
} from '@/features/discounts/hooks/useDiscounts';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Field, FieldLabel } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Zap, Trash2, Plus } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const DISCOUNT_TYPES = ['Percentage', 'FixedAmount'];

interface CustomerDiscountsDialogProps {
  customer: Customer;
}

export const CustomerDiscountDialog = ({
  customer,
}: CustomerDiscountsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    discountType: 'Percentage' as 'Percentage' | 'FixedAmount',
    discountValue: 0,
    description: '',
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const discountsQuery = useCustomerDiscounts(customer.id);
  const createMutation = useCreateCustomerDiscount(customer.id);
  const deleteMutation = useDeleteCustomerDiscount(customer.id);
  const confirm = useConfirm();

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    createMutation.mutate(
      {
        customerId: customer.id,
        ...formData,
        discountValue: formData.discountValue as number,
      },
      {
        onSuccess: () => {
          setFormData({
            discountType: 'Percentage',
            discountValue: 0,
            description: '',
          });
          setIsCreating(false);
        },
        onError: (error: any) => {
          if (error.response?.data?.errors) {
            setFieldErrors(error.response.data.errors);
          }
        },
      }
    );
  };

  const handleDelete = (discountId: string) => {
    confirm({
      title: 'Delete discount?',
      description: 'This discount will be permanently removed from the customer.',
      variant: 'destructive',
      confirmLabel: 'Delete',
      onConfirm: () =>
        withConfirmSuccess(
          deleteMutation.mutateAsync(discountId),
          'Discount deleted'
        ),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <Zap className="w-4 h-4" />
          Customer Discounts
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customer Discounts</DialogTitle>
          <DialogDescription>
            Manage individual discount overrides for {customer.customerName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Individual discounts override group default
              discounts. Leave empty to use group defaults.
            </p>
          </div>

          {/* Existing Discounts */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Current Discounts</h3>
              {!isCreating && (
                <Button
                  size="sm"
                  onClick={() => setIsCreating(true)}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Discount
                </Button>
              )}
            </div>

            {discountsQuery.isLoading ? (
              <LoadingAnimation text="Loading discounts..." />
            ) : discountsQuery.data && discountsQuery.data.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discountsQuery.data.map((discount) => (
                      <TableRow key={discount.id}>
                        <TableCell>
                          <Badge variant="outline">
                            {discount.discountType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {discount.discountType === 'Percentage'
                            ? `${discount.discountValue}%`
                            : `KES ${(discount.discountValue ?? 0).toLocaleString()}`}
                        </TableCell>
                        <TableCell className="text-sm break-words max-w-xs">
                          {discount.description}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={discount.isActive ? 'default' : 'destructive'}
                          >
                            {discount.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(discount.id)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 border rounded-lg border-dashed">
                <p>No individual discounts configured</p>
                <p className="text-xs mt-1">
                  Using group defaults from {customer.customerName}'s group
                </p>
              </div>
            )}
          </div>

          {/* Create Form */}
          {isCreating && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold mb-4">Add New Discount Override</h4>
              <form onSubmit={handleSubmit} className="space-y-4">
                {createMutation.error && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                    {createMutation.error.message}
                  </div>
                )}

                <Field>
                  <FieldLabel htmlFor="discountType">
                    Discount Type <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Select
                    value={formData.discountType}
                    onValueChange={(v) => handleChange('discountType', v)}
                    disabled={createMutation.isPending}
                  >
                    <SelectTrigger id="discountType">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOUNT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type === 'Percentage' ? 'Percentage (%)' : 'Fixed Amount (KES)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field>
                  <FieldLabel htmlFor="discountValue">
                    Discount Value <span className="text-red-500">*</span>
                  </FieldLabel>
                  <div className="flex items-center gap-2">
                    <Input
                      id="discountValue"
                      type="number"
                      step={formData.discountType === 'Percentage' ? '0.01' : '1'}
                      min="0"
                      value={formData.discountValue}
                      onChange={(e) =>
                        handleChange('discountValue', parseFloat(e.target.value) || 0)
                      }
                      required
                      disabled={createMutation.isPending}
                    />
                    <span className="text-sm text-gray-500">
                      {formData.discountType === 'Percentage' ? '%' : 'KES'}
                    </span>
                  </div>
                </Field>

                <Field>
                  <FieldLabel htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="e.g., VIP Discount, Loyalty Reward, etc."
                    required
                    disabled={createMutation.isPending}
                  />
                </Field>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="flex-1"
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Discount'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsCreating(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};